import React, { useEffect, useState } from "react";
import {
  FiHome,
  FiBarChart2,
  FiClock,
  FiUsers,
  FiCheckCircle,
  FiSettings,
  FiHelpCircle,
  FiSearch,
  FiBell,
  FiMenu,
  FiX,
  FiDownload,
  FiFilter,
  FiChevronDown,
  FiBookOpen,
  FiTarget,
  FiCalendar,
  FiLoader,
  FiPlay,
  FiAlertCircle,
} from "react-icons/fi";

const Sidebar = ({ isOpen, onClose }) => {
  const [activeItem, setActiveItem] = useState("Dashboard");

  const menuItems = [
    { name: "Dashboard", icon: FiHome, active: true },
    { name: "Analytics", icon: FiBarChart2, hasSubmenu: true },
    { name: "Time Management", icon: FiClock, active: true },
    { name: "Student", icon: FiUsers },
    { name: "Approval", icon: FiCheckCircle },
  ];

  const settingsItems = [
    { name: "Settings", icon: FiSettings, hasSubmenu: true },
    { name: "Help & Support", icon: FiHelpCircle },
  ];

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 1024) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L2 7L12 12L22 7L12 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 17L12 22L22 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 12L12 17L22 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="logo-text">Oakridge</span>
          </div>
          <button
            className="close-btn"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="sidebar-content">
          <div className="menu-section">
            <h3 className="menu-title">MAIN MENU</h3>
            <nav className="menu-nav">
              {menuItems.map((item, index) => (
                <div key={item.name} className="menu-item-wrapper">
                  <button
                    className={`menu-item ${
                      activeItem === item.name ? "active" : ""
                    }`}
                    onClick={() => setActiveItem(item.name)}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <item.icon size={18} />
                    <span>{item.name}</span>
                    {item.hasSubmenu && (
                      <FiChevronDown size={14} className="submenu-icon" />
                    )}
                  </button>
                </div>
              ))}
            </nav>
          </div>

          <div className="menu-section">
            <h3 className="menu-title">SETTINGS</h3>
            <nav className="menu-nav">
              {settingsItems.map((item, index) => (
                <div key={item.name} className="menu-item-wrapper">
                  <button
                    className={`menu-item ${
                      activeItem === item.name ? "active" : ""
                    }`}
                    onClick={() => setActiveItem(item.name)}
                    style={{
                      animationDelay: `${(menuItems.length + index) * 0.1}s`,
                    }}
                  >
                    <item.icon size={18} />
                    <span>{item.name}</span>
                    {item.hasSubmenu && (
                      <FiChevronDown size={14} className="submenu-icon" />
                    )}
                  </button>
                </div>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Overlay */
        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 998;
          backdrop-filter: blur(2px);
          display: block;
        }

        /* Sidebar */
        .sidebar {
          width: 280px;
          background: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 999;
          transform: translateX(-100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 2px 0 12px rgba(0, 0, 0, 0.1);
          border-right: 1px solid #e2e8f0;
          will-change: transform;
        }

        /* When sidebar is open */
        .sidebar.sidebar-open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          flex-shrink: 0;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .logo-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
        }

        .logo-text {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          letter-spacing: -0.025em;
        }

        .close-btn {
          display: none;
          background: none;
          border: none;
          padding: 0.5rem;
          color: #64748b;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #f1f5f9;
          color: #374151;
        }

        .close-btn:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .sidebar-content {
          flex: 1;
          padding: 1rem;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f8fafc;
        }

        .sidebar-content::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-content::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 3px;
        }

        .sidebar-content::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .sidebar-content::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .menu-section {
          margin-bottom: 2rem;
        }

        .menu-title {
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 1rem;
          padding: 0 1rem;
        }

        .menu-nav {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .menu-item-wrapper {
          position: relative;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          border: none;
          background: none;
          color: #64748b;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 100%;
          text-align: left;
          font-size: 0.875rem;
          font-weight: 500;
          position: relative;
          text-decoration: none;
        }

        .menu-item:hover {
          background: #f1f5f9;
          color: #1e293b;
          transform: translateX(2px);
        }

        .menu-item.active {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .menu-item.active:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateX(0);
        }

        .menu-item svg {
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }

        .menu-item:hover svg {
          transform: scale(1.1);
        }

        .menu-item.active svg {
          transform: scale(1);
        }

        .submenu-icon {
          margin-left: auto;
          transition: transform 0.2s ease;
        }

        .menu-item:hover .submenu-icon {
          transform: rotate(90deg);
        }

        .menu-item.active .submenu-icon {
          transform: rotate(0deg);
        }

        /* Active indicator */
        .menu-item.active::before {
          content: "";
          position: absolute;
          left: -1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 24px;
          background: white;
          border-radius: 0 2px 2px 0;
        }

        /* Focus states for accessibility */
        .menu-item:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Animation for menu items when sidebar opens */
        .sidebar.sidebar-open .menu-item {
          animation: slideInFromLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          opacity: 0;
          transform: translateX(-20px);
        }

        @keyframes slideInFromLeft {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 1023px) {
          .sidebar-overlay {
            display: block;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            left: 0;
            width: 100%;
            max-width: 280px;
          }

          .close-btn {
            display: flex;
          }

          .sidebar-header {
            padding: 1.25rem;
          }

          .logo-text {
            font-size: 1.125rem;
          }

          .menu-item {
            padding: 0.875rem 1rem;
            font-size: 0.9rem;
          }
        }

        @media (max-width: 480px) {
          .sidebar {
            width: 100vw;
            max-width: none;
          }

          .sidebar-header {
            padding: 1rem;
          }

          .sidebar-content {
            padding: 0.75rem;
          }

          .menu-title {
            font-size: 0.7rem;
            padding: 0 0.75rem;
          }

          .menu-item {
            padding: 0.75rem;
          }
        }

        /* Desktop: Always show sidebar */
        @media (min-width: 1024px) {
          .sidebar {
            position: fixed;
            transform: translateX(0) !important;
          }

          .sidebar-overlay {
            display: none !important;
          }

          .close-btn {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default Sidebar;
